// MNRL free-bottle cart guard. Spec: store/FREE_BOTTLE.md section 5. Config: snippets/mnrl-gift-guard.liquid.
// Keeps exactly one $0 gift bottle line (marked _mnrl_gift=bottle) in carts with 2+ boxes, removes it below 2.
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const cfgEl = document.getElementById('mnrl-gift-config');
const cfg = cfgEl ? JSON.parse(cfgEl.textContent) : null;
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const DECLINED_KEY = 'mnrl_gift_declined';
const SOURCE = 'mnrl-gift-guard';

let inFlight = false;
let pending = false;
let selfDispatch = false;
let timer = 0;
let giftKeys = new Set();
let lastBoxes = 0;

const declined = () => { try { return sessionStorage.getItem(DECLINED_KEY) === '1'; } catch (e) { return false; } };
const setDeclined = (on) => { try { on ? sessionStorage.setItem(DECLINED_KEY, '1') : sessionStorage.removeItem(DECLINED_KEY); } catch (e) { /* storage blocked */ } };

async function getCart() {
  const r = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  return r.json();
}

function analyze(cart) {
  const bottle = Number(cfg.bottleVariant);
  let boxes = 0;
  const gift = [];
  const paid = [];
  for (const it of cart.items || []) {
    if ((cfg.boxVariants || []).map(Number).includes(Number(it.variant_id))) boxes += it.quantity;
    if (Number(it.variant_id) === bottle) {
      if (it.properties && it.properties._mnrl_gift === 'bottle') gift.push(it);
      else paid.push(it);
    }
  }
  return { boxes, gift, paid };
}

function sectionIds() {
  return [...document.querySelectorAll('cart-items-component')].map((e) => e.dataset.sectionId).filter(Boolean);
}

// One corrective mutation, announced through the theme's standard cart event so the drawer,
// cart page and header bubble re-render the same way they do for any other cart change.
async function apply(m) {
  const ids = sectionIds();
  const body = { ...m.body };
  if (ids.length) { body.sections = ids.join(','); body.sections_url = window.location.pathname; }
  const deferred = CartLinesUpdateEvent.createPromise();
  try {
    selfDispatch = true;
    document.dispatchEvent(new CartLinesUpdateEvent({ action: m.action, context: 'cart', lines: m.lines, promise: deferred.promise, detail: { source: SOURCE } }));
  } catch (e) {
    console.warn('[mnrl-gift-guard] event dispatch failed', e);
  } finally {
    selfDispatch = false;
  }
  try {
    const r = await fetch(`${ROOT}cart/${m.kind}.js`, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const json = await r.json();
    if (!r.ok || json.status) throw new Error(json.description || json.message || `cart/${m.kind} failed`);
    const cart = m.kind === 'add' ? await getCart() : json;
    deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: json.sections, items: cart.items, itemCount: cart.item_count, source: SOURCE, didError: false } });
    return true;
  } catch (e) {
    console.warn('[mnrl-gift-guard]', e);
    deferred.resolve({ cart: null, detail: { didError: true, source: SOURCE } });
    if (cfg.template === 'cart' && !sectionIds().length) window.location.reload();
    return false;
  }
}

function plan(s) {
  const bottle = String(cfg.bottleVariant);
  if (s.boxes < cfg.minBoxes && s.gift.length) {
    return { kind: 'change', action: 'remove', body: { id: s.gift[0].key, quantity: 0 }, lines: [{ id: s.gift[0].key, quantity: 0 }] };
  }
  if (s.gift.length > 1) {
    const extra = s.gift[s.gift.length - 1];
    return { kind: 'change', action: 'remove', body: { id: extra.key, quantity: 0 }, lines: [{ id: extra.key, quantity: 0 }] };
  }
  if (s.gift.length === 1 && s.gift[0].quantity > 1) {
    return { kind: 'change', action: 'update', body: { id: s.gift[0].key, quantity: 1 }, lines: [{ id: s.gift[0].key, quantity: 1 }] };
  }
  if (s.boxes >= cfg.minBoxes && !s.gift.length && !s.paid.length && !declined()) {
    return { kind: 'add', action: 'add', body: { items: [{ id: Number(bottle), quantity: 1, properties: { _mnrl_gift: 'bottle' } }] }, lines: [{ merchandiseId: bottle, quantity: 1 }] };
  }
  return null;
}

async function reconcile(depth = 0) {
  if (!cfg) return;
  if (inFlight) { pending = true; return; }
  inFlight = true;
  let changed = false;
  try {
    const s = analyze(await getCart());
    if (s.boxes < cfg.minBoxes) setDeclined(false);
    giftKeys = new Set(s.gift.map((g) => g.key));
    lastBoxes = s.boxes;
    const m = plan(s);
    if (m) changed = await apply(m);
  } catch (e) {
    console.warn('[mnrl-gift-guard] reconcile failed', e);
  } finally {
    inFlight = false;
  }
  // Converge (e.g. duplicate gift lines + below threshold) with a hard cap.
  if ((changed && depth < 3) || pending) { pending = false; reconcile(changed ? depth + 1 : 0); }
}

function schedule() {
  clearTimeout(timer);
  timer = setTimeout(() => reconcile(), 300);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (selfDispatch || (event.detail && event.detail.source === SOURCE)) return;
  // Customer removed the gift while still qualifying: respect it for this session.
  try {
    const lines = event.lines || [];
    const removedGift = lines.some((l) => Number(l.quantity) === 0 && [...giftKeys].some((k) => String(l.id).includes(k)));
    if (removedGift && lastBoxes >= cfg.minBoxes) setDeclined(true);
  } catch (e) { /* ignore */ }
  const settle = event.promise ? event.promise.then(() => {}, () => {}) : Promise.resolve();
  settle.then(schedule);
});

// Run on load when the cart has items (also covers /cart and cart permalinks), and whenever the drawer opens.
if (cfg && (cfg.template === 'cart' || Number(cfg.cartCount) > 0)) reconcile();
document.querySelector('theme-drawer#cart-drawer')?.addEventListener('theme-drawer:open', () => schedule());
